import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  CustomDefectDropdownItem,
  DefectTypeDropdownItem,
} from '@/components/DefectsList/DefectTypeDropdownItem';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useCreateDefect } from '@/models/article';
import { DefectTypeFromGetMany, useDefectTypes } from '@/models/defectType';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

import Loader from '../Loader';

export const DefectTypeSearchDialog = ({
  isOpen,
  onClose,
  onDefectCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDefectCreated: (createdDefectId: string) => void;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({ id: 'defect-type-search-dialog.add-defect.label', message: 'Add a defect' })}
      isOpen={isOpen}
      onOpenChange={onClose}
      fullHeight
    >
      <DefectTypeSearchDialogContent onDefectCreated={onDefectCreated} />
    </Dialog>
  );
};

const DefectTypeSearchDialogContent = ({
  onDefectCreated,
}: {
  onDefectCreated: (createdDefectId: string) => void;
}) => {
  const { t } = useLingui();
  const { article, request, view } = useArticleContext();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);
  const [hasClickedCustom, setHasClickedCustom] = useState(false);

  const { data: defectTypes = [], isFetching } = useDefectTypes(
    {
      articleId: article.id,
      query: debouncedQuery ?? undefined,
    },
    {
      keepPreviousData: true,
    }
  );

  const {
    mutateAsync: createDefect,
    isPending: isPendingCreateDefect,
    isSuccess: isSuccessCreateDefect,
  } = useCreateDefect({
    requestId: request.id,
    articleId: article.id,
  });

  const handleCreateDefect = async (defect: DefectTypeFromGetMany | 'custom') => {
    const { id } = await createDefect(
      defect === 'custom'
        ? {
            description: '',
          }
        : {
            defectTypeOrganization: defect,
          }
    );

    onDefectCreated(id);
  };

  const isPending = isPendingCreateDefect || isSuccessCreateDefect;

  return (
    <>
      <main>
        <Stack gap="0.5rem">
          <InputSearch
            aria-label={t({
              id: 'defect-type-search-dialog.add-defect.label',
              message: 'Add a defect',
            })}
            placeholder={t({
              id: 'defect-type-search-dialog.add-defect.placeholder',
              message: 'Search a defect to add...',
            })}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            isLoading={isFetching || query !== debouncedQuery}
            value={query}
            onChange={setQuery}
          />

          {defectTypes.length === 0 && (
            <InlineText color="secondary" style={{ padding: '0.5rem 0' }}>
              <Trans id="defect-type-search-dialog.no-results">
                No results matching your search
              </Trans>
            </InlineText>
          )}

          <DefectTypesMenu
            defectTypeOptions={defectTypes}
            onChooseDefectType={handleCreateDefect}
            isPending={isPending}
          />
        </Stack>
      </main>

      {view.defects.customEnabled && (
        <Footer>
          <FooterButtons>
            <Button
              variant="secondary"
              onPress={() => {
                setHasClickedCustom(true);
                handleCreateDefect('custom');
              }}
              disabled={isPending && !hasClickedCustom}
              isLoading={isPending && hasClickedCustom}
            >
              <CustomDefectDropdownItem style={{ display: 'flex', justifyContent: 'center' }} />
            </Button>
          </FooterButtons>
        </Footer>
      )}
    </>
  );
};

const DefectTypesMenu = ({
  defectTypeOptions,
  onChooseDefectType,
  isPending,
}: {
  defectTypeOptions: DefectTypeFromGetMany[];
  onChooseDefectType: (defectType: DefectTypeFromGetMany) => void;
  isPending: boolean;
}) => {
  return (
    <Stack>
      {defectTypeOptions.map((defectTypeOption) => (
        <DefectTypeMenuItem
          key={defectTypeOption.id}
          defectTypeOption={defectTypeOption}
          onChooseDefectType={onChooseDefectType}
          isPending={isPending}
        />
      ))}
    </Stack>
  );
};

const DefectTypeMenuItem = ({
  defectTypeOption,
  onChooseDefectType,
  isPending,
}: {
  defectTypeOption: DefectTypeFromGetMany;
  onChooseDefectType: (defectType: DefectTypeFromGetMany) => void;
  isPending: boolean;
}) => {
  const [hasClicked, setHasClicked] = useState(false);

  return (
    <Button
      style={{ padding: '0.5rem' }}
      variant="style-less"
      onPress={() => {
        onChooseDefectType(defectTypeOption);
        setHasClicked(true);
      }}
      disabled={isPending}
    >
      <Stack row style={{ flex: 1 }} alignItems="center">
        <DefectTypeDropdownItem defect={defectTypeOption} />
        {isPending && hasClicked && <Loader />}
      </Stack>
    </Button>
  );
};
